import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("ecodecide_token"));

  const initAuth = useCallback(async () => {
    // Check for JWT token first
    if (token) {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data);
        setLoading(false);
        return;
      } catch (error) {
        console.error("Token validation failed:", error.message);
        localStorage.removeItem("ecodecide_token");
        setToken(null);
      }
    }
    
    // Fallback: check for Google session
    const storedUser = localStorage.getItem("ecodecide_user");
    if (storedUser) {
      try {
        const response = await axios.get(`${API}/auth/me/session`, {
          withCredentials: true
        });
        setUser(response.data);
        setLoading(false);
        return;
      } catch (error) {
        console.error("Session validation failed:", error.message);
        localStorage.removeItem("ecodecide_user");
      }
    }
    
    setLoading(false);
  }, [token]);

  useEffect(() => {
    initAuth();
  }, [initAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("ecodecide_token", newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (data) => {
    const response = await axios.post(`${API}/auth/register`, data);
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("ecodecide_token", newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    // Logout from backend (clears session cookie)
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout request failed:", error.message);
    }
    
    localStorage.removeItem("ecodecide_token");
    localStorage.removeItem("ecodecide_user");
    setToken(null);
    setUser(null);
  };

  const refreshUser = useCallback(async () => {
    // Try JWT token first
    if (token) {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data);
        return;
      } catch (error) {
        console.error("Refresh user (JWT) failed:", error.message);
      }
    }
    
    // Fallback to session
    try {
      const response = await axios.get(`${API}/auth/me/session`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      console.error("Refresh user (session) failed:", error.message);
    }
  }, [token]);

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, refreshUser, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
